/**
 * session.types.ts — Shared types and constants for the multitasking WebSocket pool.
 *
 * This is the single source of truth for all session-level concepts: status lifecycle,
 * categorisation, per-session data shape, and pool-wide event signatures.
 *
 * Consumers should import from here rather than from individual hook files
 * to facilitate future refactoring.
 */

// ============================================================================
// Imports
// ============================================================================

import type { MemoryUsage, BackendTool } from '../types/websocket.types';
import type {
  AvailableActCatalogEntry,
  ConversationCaseRefusal,
  ConversationCasesPayload,
  ResolvedAgentUiAction,
} from '@hector/shared-types';
import type { LegalQuestion, Jurisdiction, FondementJuridique, JurisprudenceSource, QuestionProgress } from '../components/jurisprudence/types';
import type { ParsedJurisprudence } from '@/utils/jurisprudenceParser';
import type { SessionDoneSnapshot } from './transcript.types';
import type {
  RedactionPlanDraftSnapshot,
  RedactionPlanDraftUpdated,
  SkillChangeRequest,
  SkillChangeResult,
} from '@hector/shared-types';

// ============================================================================
// Timeline types — consumed by StreamingSessionReducer and useSession
// ============================================================================

/** Thinking state exposed to the UI */
export interface ThinkingState {
  content: string;
  isActive: boolean;
  startedAt: number;
  durationMs?: number;
}

/** Tool event exposed to the UI — mirrors BackendTool['payload'] + id */
export interface ToolEvent {
  id: string;
  tool: BackendTool['payload']['tool'];
  status: BackendTool['payload']['status'];
  data?: BackendTool['payload']['data'];
  timestamp: string;
  /**
   * Identité stable de la carte = `invocation_id` backend (tool_use.id) quand
   * disponible. Permet de corréler les events started→completed/error sur la
   * BONNE carte quand plusieurs invoke_subagent concurrents portent le même
   * `tool`. Absent pour les tools qui n'émettent pas d'invocation_id.
   */
  invocation_id?: string;
  /**
   * Marqueur local posé lorsque la socket s'interrompt pendant l'outil.
   * Ce n'est pas une erreur métier : une frame corrélée rejouée à la
   * reconnexion peut encore fixer le statut terminal réel.
   */
  transportInterrupted?: true;
}

/**
 * Verdict backend faisant autorité pour une réinitialisation de l'Analyse avancée.
 * Le succès d'envoi WebSocket ne vaut jamais autorisation ni succès du reset :
 * la carte devient terminale uniquement sur ce verdict corrélé par `requestId`.
 */
export interface CaseWikiResetVerdict {
  launched: boolean;
  jobId?: string;
  alreadyRunning?: boolean;
  declined?: boolean;
  timedOut?: boolean;
  error?: boolean;
  userMessage?: string;
}

export interface DeletePiecesConfirmationVerdict {
  deleted: boolean;
  deletedCount?: number;
  s3ErrorCount?: number;
  declined?: boolean;
  timedOut?: boolean;
  error?: boolean;
  userMessage?: string;
}

/** A single item in the chronological timeline */
export type TimelineItem =
  | { type: 'thinking'; id: string; thinking: ThinkingState }
  | { type: 'tool'; id: string; event: ToolEvent }
  | { type: 'content'; id: string; content: string }
  | { type: 'jurisprudence_form'; id: string; jobId: string; questions: LegalQuestion[]; jurisdictions: Jurisdiction[]; fondements: FondementJuridique[]; autoApprove?: boolean }
  | { type: 'jurisprudence_progress'; id: string; jobId: string; questions: QuestionProgress[] }
  | { type: 'jurisprudence_results'; id: string; jobId: string; responseText: string; sources: JurisprudenceSource[]; jurisprudences: ParsedJurisprudence[]; fondements: FondementJuridique[] }
  | {
      type: 'case_wiki_reset';
      id: string;
      caseId: string;
      requestId: string;
      caseTitle?: string | null;
      clientName?: string | null;
      caseWikiContextUserInputs?: string;
      result?: CaseWikiResetVerdict;
    }
  | {
      type: 'delete_pieces_confirmation';
      id: string;
      caseId: string;
      caseTitle?: string;
      requestId: string;
      pieces: Array<{ documentId: string; title: string; pieceNumber?: string }>;
      result?: DeletePiecesConfirmationVerdict;
    }
  | {
      type: 'redaction_plan_form';
      id: string;
      requestId: string;
      jobId: string;
      kind: 'research';
      /** Optional only while the frontend can be deployed before the backend. */
      autoLaunchAt?: string;
      /** Latest server-accepted draft revision used to calculate the deadline. */
      autoLaunchRevision?: number;
      /** Server-accepted draft rendered again after an ACK-triggered remount. */
      autoLaunchDraftSnapshot?: RedactionPlanDraftSnapshot;
      /** Latest draft update awaiting backend ACK; replayed after reconnection. */
      autoLaunchDraftUpdatePending?: RedactionPlanDraftUpdated['payload'];
      proposedPayload: {
        actId?: string;
        nom_acte?: string | null;
        specialisation?: string | null;
        juridictions?: string[];
        additional_instructions?: string;
        original_job_id?: string;
        generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
        /** Toggle UI "désactiver recherche JP" — snapshot du clic Lancer. */
        skip_jurisprudence?: boolean;
        /** Lancement en un seul temps : le job produit l'acte, sans plan intermédiaire. */
        full_document?: boolean;
        /** Hiérarchie canonique complète, annotée de la disponibilité exacte. */
        availableActCatalog?: AvailableActCatalogEntry[];
      };
    }
  | {
      type: 'redaction_plan_progress';
      id: string;
      requestId: string;
      jobId: string;
      kind: 'research' | 'generation';
      /**
       * Phase courante. `preparation` = le worker compose encore le plan ou l'acte, rien ne
       * s'écrit dans le canvas ; `insertion` ne commence qu'au signal `redaction_job_started`.
       */
      phase: 'planning' | 'research' | 'aggregation' | 'preparation' | 'insertion' | 'complete' | 'error';
      /** Titres des arguments si identifiés (research-only). */
      argumentTitles?: string[];
      /** Compteur de queries terminées (research-only). */
      completedQueries?: number;
      totalQueries?: number;
      /**
       * Liste détaillée des queries de recherche JP — populée à partir des
       * events `search_query_start/fetch_complete/relevance_batch_progress/
       * search_query_complete` du worker. Consommée par
       * `RedactionQueriesCollapsible` (cards collapsibles par argument avec
       * cercle de progression). Vide tant que le worker est en planning ou
       * que `skipJurisprudence === true`.
       */
      searchQueries?: import('@/features/redaction/types/research-progress').SearchQuery[];
      /**
       * État de vérification Légifrance des fondements juridiques (events
       * `legal_verification_start/complete`). Affiché en badge dans le
       * `RedactionArgumentsCollapsible`.
       */
      legalVerification?: import('@/features/redaction/types/research-progress').LegalVerificationState;
      /**
       * Toggle « désactiver recherche JP », figé à la décision de lancement.
       * Prend la valeur soumise par l'avocat après un clic manuel, ou la
       * proposition du LLM lors du lancement automatique.
       *
       * Quand `true`, on n'affiche que 2 pastilles (Arguments + Insertion) et on saute les
       * blocs research/aggregation : `aggregation_start` n'étant émis que par les services
       * de jurisprudence, la phase « Synthèse » ne survient jamais sans recherche JP.
       */
      skipJurisprudence?: boolean;
      /** Erreur transient ou fatale. */
      errorMessage?: string;
      /**
       * Dernière étape réellement atteinte avant l'échec.
       *
       * `phase` bascule sur `'error'` et perd donc l'information. La conserver
       * est ce qui permet de dire à l'avocat OÙ ça a cassé (« pendant la
       * recherche de jurisprudence ») plutôt qu'un « Erreur du job » opaque.
       */
      errorPhase?: 'planning' | 'research' | 'aggregation' | 'preparation' | 'insertion';
      /** Snapshot du payload proposé (pour récap visuel pendant le job). */
      proposedPayload: {
        nom_acte?: string | null;
        specialisation?: string | null;
        juridictions?: string[];
        additional_instructions?: string;
        original_job_id?: string;
        generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
        /** Toggle UI "désactiver recherche JP" — snapshot du clic Lancer. */
        skip_jurisprudence?: boolean;
        /** Lancement en un seul temps : le job produit l'acte, sans plan intermédiaire. */
        full_document?: boolean;
      };
      /**
       * Verdict en attente — set quand le backend a émis `redaction_plan_results`
       * MAIS que le SSE canvas n'a pas encore terminé son insertion. La carte
       * reste en mode progress jusqu'à ce que le SSE onComplete frontend fire.
       * Cf. RedactionJobCollector backend (sendRedactionJobStarted post-completion)
       * + applyRedactionPlanSseComplete reducer (flip diféré).
       * Mirror du pattern Word add-in : la fin de carte = fin job + fin SSE.
       */
      pendingVerdict?: 'completed' | 'declined' | 'interrupted' | 'cancelled' | 'error';
      pendingExtras?: Record<string, unknown>;
    }
  | {
      type: 'redaction_plan_results';
      id: string;
      requestId: string;
      jobId: string;
      kind: 'research' | 'generation';
      verdict: 'completed' | 'declined' | 'interrupted' | 'cancelled' | 'error';
      /** Snapshot pour le récap visuel post-job. */
      proposedPayload: {
        nom_acte?: string | null;
        specialisation?: string | null;
        juridictions?: string[];
        additional_instructions?: string;
        original_job_id?: string;
        generation_mode?: 'full' | 'section_i' | 'section_ii_iii';
        /** Toggle UI "désactiver recherche JP" — snapshot du clic Lancer. */
        skip_jurisprudence?: boolean;
        /** Lancement en un seul temps : le job produit l'acte, sans plan intermédiaire. */
        full_document?: boolean;
      };
      /** Extras du verdict (arguments_count, message…). */
      extras?: Record<string, unknown>;
      /**
       * Snapshot des données de progression capturé au flip progress → results.
       * Sans lui, arguments/recherches JP étaient PERDUS au terminal — le
       * bandeau final peut désormais se déplier vers l'UI complète de
       * génération (demande produit 2026-07-13). Absent quand le verdict
       * arrive sans carte progress (cas C) ou avant toute donnée.
       */
      progressSnapshot?: {
        argumentTitles?: string[];
        completedQueries?: number;
        totalQueries?: number;
        searchQueries?: import('@/features/redaction/types/research-progress').SearchQuery[];
        legalVerification?: import('@/features/redaction/types/research-progress').LegalVerificationState;
        skipJurisprudence?: boolean;
      };
    }
  /**
   * Sorties cliquables proposées par l'agent en fin de tour. Contrairement à
   * la confirmation de suppression de pièces, il n'y a **ni `requestId` ni
   * `result`** : aucun agent n'attend la réponse, le clic est une navigation
   * locale idempotente pilotée par l'hôte qui monte le chat.
   */
  | { type: 'ui_actions'; id: string; actions: ResolvedAgentUiAction[] }
  | { type: 'skill_change'; id: string; request: SkillChangeRequest; result?: SkillChangeResult }
  | { type: 'disconnected'; id: string }
  | { type: 'agent_error'; id: string; message: string; source?: 'agent_done' | 'backend_error' | 'reconnect'; backendCode?: string }
  | { type: 'rate_limit_note'; id: string; message: string }
  // Arrêt volontaire (clic Stop) — note discrète, jamais un bandeau d'erreur amber.
  | { type: 'user_stopped'; id: string }
  // Tour toujours en cours hors de portée de ce socket (autre instance backend, ou fenêtre
  // de replay dépassée) : note discrète, le résultat arrive par rechargement de l'historique.
  // `cause` absent = l'avocat a perdu sa connexion (cas historique).
  | { type: 'server_finishing'; id: string; cause?: ServerFinishingCause };

/**
 * Pourquoi cette page attend un tour qu'elle ne peut pas suivre, quand ce n'est PAS une
 * coupure. `open_elsewhere` : une autre socket vivante tient le tour (même conversation
 * ouverte dans un autre onglet ou sur un autre appareil, prod 2026-10-09). Union fermée :
 * chaque cause porte son propre libellé dans `StreamingTimeline`.
 */
export type ServerFinishingCause = 'open_elsewhere';

// ============================================================================
// Constants
// ============================================================================

/** Maximum number of concurrently streaming sessions. */
export const MAX_ACTIVE_SLOTS = 3;

// WHY no timer constants: the backend now owns all timeouts (heartbeat 15s ACK,
// inactivity 30min, grace period 15s). The frontend has ZERO timers — it reacts
// to backend events (session_expired, agent_done with server_shutdown, etc.).

// ============================================================================
// SessionStatus
// ============================================================================

/**
 * Lifecycle status of a single WebSocket session.
 *
 * Simplified from 7 to 4 states. The backend now handles all timeouts.
 *
 * Transitions:
 *   connecting → ready | streaming | error
 *   ready      → streaming | error             (user sends new message)
 *   streaming  → ready | error                 (agent_done or session_expired)
 *   error      → (terminal — no recovery from frontend)
 *
 * WHY only 4 states:
 * - `idle` renamed to `ready` — session is alive and accepting messages.
 * - `done` removed — backend handles session lifecycle, frontend stays `ready`.
 * - `timed_out` removed — backend sends `session_expired` → frontend goes `error`.
 * - `waiting_user_action` removed — JP form display is a sub-state of `streaming`,
 *   detected via timeline items (type === 'jurisprudence_form').
 */
export type SessionStatus =
  | 'connecting'
  | 'ready'      // was 'idle' — session is alive and accepting messages
  | 'streaming'  // includes JP form display as sub-state (detected via timeline)
  | 'error';     // terminal — reconnection failed, agent error, etc.

/**
 * Whether the session has an active exchange in flight.
 * Used by disconnect/reconnect handlers to decide whether an interrupted
 * exchange needs recovery.
 */
export function isExchangeActive(status: SessionStatus | null): boolean {
  return status === 'streaming';
}

// ============================================================================
// SessionCategory — coarser grouping used by the pool to manage slot allocation
// ============================================================================

/**
 * Coarse grouping of session statuses for slot-management decisions.
 *
 * - `occupied`   — session is actively using a WebSocket connection.
 * - `standby`    — session exists but is not streaming; slot may be freed.
 * - `terminated` — session has reached a terminal state and will be evicted.
 */
export type SessionCategory = 'occupied' | 'standby' | 'terminated';

/**
 * Maps a fine-grained `SessionStatus` to its coarse `SessionCategory`.
 *
 * Used by the pool to decide whether a session occupies an active slot,
 * is dormant, or is eligible for cleanup.
 */
export function getSessionCategory(status: SessionStatus | null): SessionCategory {
  if (!status) return 'terminated';
  switch (status) {
    case 'connecting':
    case 'streaming':
      return 'occupied';
    case 'ready':
      return 'standby';
    case 'error':
      return 'terminated';
  }
}

// ============================================================================
// Slot eviction — which session to drop when the pool is full
// ============================================================================

/** Minimal slot shape needed to decide eviction. */
export interface SlotEvictionCandidate {
  sessionKey: string;
  status: SessionStatus;
  /** `Date.now()` at slot creation — used to pick the oldest. */
  createdAt: number;
  /**
   * Une compaction manuelle n'est pas soldée sur ce slot (demande en attente
   * d'ACK ou de verdict). La session reste `ready` — la compaction a lieu hors
   * tour —, mais c'est un échange actif pour l'éviction.
   */
  hasContextCompactionInFlight: boolean;
}

/**
 * Pick which slot to evict to free capacity for a NEW session the user is
 * actively requesting. Returns the sessionKey to evict, or `null` if nothing
 * is reclaimable.
 *
 * Only `ready` sessions are reclaimable here: `connecting` can already have a
 * first prompt queued while WS auth / `connection_established` is still in
 * flight, and this pool has no stale/exhausted marker to distinguish that live
 * handshake from a truly wedged one. Evicting it would drop the queued prompt.
 * `streaming` is also never reclaimable because real work is in flight.
 *
 * Une session `ready` qui compacte sa mémoire ne l'est pas davantage. L'évincer
 * effaçait en silence le rond et le verdict, pendant que le backend poursuivait
 * la compaction sur la socket fermée ; un message suivant dans ce fil, sur une
 * nouvelle socket, avortait alors l'orphelin avant persistance. L'avocat croyait
 * avoir compacté et rien n'avait eu lieu. Pool plein de tels slots : l'ouverture
 * est refusée et l'avocat en est informé, comme pendant des réponses en cours.
 */
export function pickSlotToEvict(candidates: SlotEvictionCandidate[]): string | null {
  const oldestOfStatus = (status: SessionStatus): SlotEvictionCandidate | null => {
    let oldest: SlotEvictionCandidate | null = null;
    for (const c of candidates) {
      if (c.hasContextCompactionInFlight) continue;
      if (c.status === status && (!oldest || c.createdAt < oldest.createdAt)) {
        oldest = c;
      }
    }
    return oldest;
  };

  return oldestOfStatus('ready')?.sessionKey ?? null;
}

// ============================================================================
// ReconnectInfo — progress feedback during automatic reconnection
// ============================================================================

/** Describes the current state of an in-progress reconnection attempt. */
export interface ReconnectInfo {
  /** Current attempt number (1-based). */
  attempt: number;
  /** Maximum number of attempts before giving up. */
  maxAttempts: number;
  /** Seconds remaining until the next attempt (0 while attempting). */
  countdown: number;
}

// ============================================================================
// ConnectionState — WebSocket connection health for a single session
// ============================================================================

/** Connection health snapshot exposed to session consumers. */
export interface ConnectionState {
  /** Whether the WebSocket is currently open and authenticated. */
  isConnected: boolean;
  /** Non-null while automatic reconnection is in progress. */
  reconnectInfo: ReconnectInfo | null;
  /** True once all reconnection attempts have been exhausted. */
  reconnectFailed: boolean;
}

// ============================================================================
// ContextCompactionState — compaction manuelle de la mémoire (hors tour)
// ============================================================================

/**
 * Phase d'une compaction MANUELLE demandée depuis le rond du composer.
 *
 * `completed` / `error` / `refused` sont des états transitoires : le pool les
 * publie (le pont en tire le toast et le rafraîchissement de l'historique) puis
 * repasse aussitôt à `idle`. Aucun de ces états ne touche `SessionStatus` : la
 * compaction a lieu HORS tour, donc jamais `streaming`.
 */
export type ContextCompactionPhase = 'idle' | 'running' | 'completed' | 'error' | 'refused';

/** Dernier état publié par le pool pour la compaction manuelle d'une session. */
export interface ContextCompactionState {
  status: ContextCompactionPhase;
  /** Renseignés sur `completed` quand le backend les fournit. */
  tokensBefore?: number;
  tokensAfter?: number;
  /** Message français du backend sur `error` / `refused`. */
  userMessage?: string;
}

/** État de repos partagé (référence stable pour les sélecteurs). */
export const IDLE_CONTEXT_COMPACTION: ContextCompactionState = Object.freeze({ status: 'idle' });

/**
 * Dernière réponse d'une socket authentifiée sur `capabilities.compactContext`.
 *
 * `unknown` tant qu'aucune socket n'a répondu (conversation rouverte sans
 * socket, authentification en cours) : le bouton est offert, et le clic attend
 * la réponse. `unsupported` est collant jusqu'au prochain ACK qui dirait
 * l'inverse : une coupure ne rend pas le bouton à un backend qui l'a refusé.
 */
export type CompactContextCapability = 'unknown' | 'supported' | 'unsupported';

// ============================================================================
// SessionData — full runtime state of one session slot
// ============================================================================

/**
 * All runtime state owned by a single session slot in the pool.
 *
 * Stored in the Zustand `activeSessionsStore` under `sessions[sessionKey]`.
 */
export interface SessionData {
  /** Stable identifier for the slot (e.g. `"session-1"`). */
  sessionKey: string;

  /** Supabase conversation UUID, or null before the first exchange completes. */
  conversationId: string | null;

  /** Optional dossier UUID this session is scoped to. */
  caseId: string | null;

  /** Current lifecycle status. */
  status: SessionStatus;

  /**
   * Live timeline for the current (ongoing or most recent) exchange.
   * Cleared at the start of each new `sendMessage` call.
   */
  timeline: TimelineItem[];


  /** Latest context window usage snapshot from `context_usage_update` events. */
  memoryUsage: MemoryUsage | null;

  /** Dernière réponse d'une socket de cette session sur la compaction manuelle. */
  compactContextCapability: CompactContextCapability;

  /** Compaction manuelle en cours ou venant d'aboutir (cf. {@link ContextCompactionState}). */
  compaction: ContextCompactionState;

  /**
   * `Date.now()` timestamp of the last activity in this session.
   * Updated on every incoming WS event and on user actions.
   */
  lastActivityAt: number;

  /**
   * `Date.now()` origin of the CURRENT agent turn — origin of the feather timer.
   * Set when the turn becomes active ('streaming'), preserved across reconnects,
   * cleared (null) when the turn ends ('ready'/'error').
   *
   * WHY it lives in the session and not in StreamingTimeline local state: the
   * timeline component remounts on conversation switch (keyed by exchange id), so
   * a local `Date.now()` origin would reset to "time since the conversation was
   * opened". Storing it per-session means a turn that started in the background
   * shows its REAL elapsed time when the user comes back to it. (2026-06-22)
   */
  turnStartedAt: number | null;
}

// ============================================================================
// Signal d'écriture agent dans le canvas (#1588)
// ============================================================================

/**
 * La trame qui a fait écrire Hector dans le canvas d'un fil. Une par point
 * d'émission de `WebSocketPool` : le nom dit où chercher quand un signal manque
 * ou surprend.
 */
export type CauseDEcritureAgentCanvas =
  | 'canvas_open_request'
  | 'canvas_open_document_request'
  | 'canvas_load_template_request'
  | 'canvas_perform_action'
  | 'canvas_generation_request'
  | 'redaction_job_started';

/**
 * « Hector commence à écrire dans le canvas de ce fil. »
 *
 * Émis par le seul `WebSocketPool`, après les refus du TRANSPORT (époque,
 * ouverture, liaison) : jamais pour un canvas qui n'a pas pu s'ouvrir. Pour une
 * action et une génération, il part AVANT l'écriture elle-même, pour que
 * l'avocat la voie arriver : un refus de l'exécuteur ou du point d'écriture
 * (révision périmée, remplacement invisible…) survient donc après lui et ne
 * retire pas la bascule. Écouté par le seul dock, qui bascule alors vers
 * l'assistant (le dock n'offre aucun emplacement au canvas).
 */
export interface SignalDEcritureAgentCanvas {
  /** Clé opaque de la connexion qui porte le fil — seule identité d'un fil pas encore confirmé. */
  readonly sessionKey: string;
  readonly conversationId: string;
  readonly cause: CauseDEcritureAgentCanvas;
  /**
   * `job:<jobId>` ou `requete:<requestId>`. Une même clé, sur un même fil, ne
   * produit qu'un signal : une trame rejouée (doublon backend, reconnexion) ne
   * doit pas rebasculer l'avocat.
   */
  readonly cleDIdempotence: string;
}

/**
 * « Hector prend le document de ce fil » — avant de le déployer, de le lire ou
 * d'y écrire (#1588).
 *
 * Émis par le seul `WebSocketPool`, après les refus préparatoires et juste AVANT
 * son déploiement. Les admissions ordinaires attendent leur résidence prête. Un
 * chargement DOCX froid, lui, émet après réservation de sa graine mais avant sa
 * résidence : son premier SuperDoc doit naître dans le slot assistant visible,
 * que le signal fait préparer. Écouté par le seul dock, qui publie alors la
 * scène du canvas pour ce fil : le déploiement la lit aussitôt, et une scène
 * restée sur le fil d'un écran précédent lui ferait sélectionner ce fil-là. Le
 * dock ne la publie QU'ICI — le faire dès qu'il affiche un fil réhydratait ou
 * refroidissait des documents sur une page qui n'en montre aucun ; le faire
 * avant une recherche ou un montage refusé déplacerait la scène sans document
 * admis.
 */
export type SignalDAdmissionAgentCanvas =
  | {
    /** Clé opaque de la connexion qui porte le fil — seule identité d'un fil pas encore confirmé. */
    readonly sessionKey: string;
    readonly conversationId: string;
    /**
     * Le prochain montage doit créer SuperDoc dans un slot assistant déjà réel.
     *
     * Le chargement DOCX froid ne peut pas suivre la transition douce du dock :
     * SuperDoc mémorise ses mesures au parking, puis reste visuellement blanc
     * après le déplacement. Cette marque prépare donc l'écran AVANT le montage ;
     * les admissions ordinaires conservent leur comportement et n'envoient rien.
     */
    readonly requiresVisibleSlot: true;
    /** Ce seul signal anticipe le montage d'un DOCX froid. */
    readonly cause: 'canvas_load_template_request';
    /** Une trame DOCX rejouée ne doit pas réarmer la transition de l'écran. */
    readonly cleDIdempotence: string;
  }
  | {
    /** Clé opaque de la connexion qui porte le fil — seule identité d'un fil pas encore confirmé. */
    readonly sessionKey: string;
    readonly conversationId: string;
    readonly requiresVisibleSlot?: never;
  };

// ============================================================================
// PoolEvents — typed event map for the PoolEventEmitter
// ============================================================================

/**
 * Typed event signatures emitted by the WebSocketPool via PoolEventEmitter.
 *
 * Key = event name, Value = tuple of argument types passed to listeners.
 */
export interface PoolEvents {
  /** The live timeline for a session has received new items. */
  timeline_updated: [sessionKey: string, timeline: TimelineItem[]];
  /** The session status has changed. */
  status_changed: [sessionKey: string, status: SessionStatus];
  /** The WebSocket connectivity state (or upcoming reconnect) changed. */
  connection_changed: [sessionKey: string, connected: boolean, reconnectInfo?: ReconnectInfo];
  /** The agent for a session has finished its work. */
  session_done: [snapshot: SessionDoneSnapshot];
  /** La trame `conversation_cases` d'une session : la liste des dossiers remplace la précédente. */
  conversation_cases_changed: [sessionKey: string, payload: ConversationCasesPayload];
  /** Le serveur a refusé un message à cause des dossiers de la conversation : aucune trame de liste ne suit. */
  case_scope_refused: [sessionKey: string, refusal: ConversationCaseRefusal];
  /** The backend confirmed the conversation ID for a new session. */
  conversation_confirmed: [sessionKey: string, conversationId: string];
  /** Claude context-window usage updated for a session. */
  memory_usage_updated: [sessionKey: string, usage: MemoryUsage];
  /** Un ACK générique a répondu sur la trame `compact_context` (jamais `unknown`). */
  compact_context_capability_changed: [sessionKey: string, capability: CompactContextCapability];
  /**
   * Nouvel état de la compaction manuelle. Un état terminal (`completed`,
   * `error`, `refused`) est toujours suivi d'un `idle`.
   */
  context_compaction_changed: [sessionKey: string, conversationId: string, state: ContextCompactionState];
  /**
   * L'historique persisté a changé hors d'un tour local (compaction manuelle
   * aboutie, ou compaction dont l'issue a été perdue avec la socket) : la base
   * REST fait foi, le pont marque le transcript périmé et recharge l'historique.
   */
  conversation_history_stale: [sessionKey: string, conversationId: string];
  /**
   * The session was fully torn down and removed from the pool (`cleanup()`):
   * inactivity expiry, slot eviction, manual close, or a failed initial connect.
   * The bridge mirrors this into the store (`removeSession`/`removeConnection`)
   * so no stale (dead) session key survives — see `WebSocketPool.cleanup`.
   */
  session_removed: [sessionKey: string];
  /**
   * A mutating pièce/folder agent tool completed server-side — set_piece_numbers
   * (renumber), create_case_folders, move_pieces, rename_pieces, rename_folders,
   * delete_pieces, save_document or save_jurisprudence.
   * The bridge invalidates case-documents / folder-contents / folder-tree queries
   * so the pièces panel refreshes without a manual reload.
   */
  case_documents_mutated: [sessionKey: string, caseId: string | null];
  /**
   * L'agent a rendu un dossier par `create_case` : dossier créé, ou dossier
   * existant repris. Le pont rafraîchit les listes de dossiers (sélecteur du
   * composeur, page Dossiers) sans rechargement. La liste des dossiers de la
   * conversation, elle, vient de la trame `conversation_cases`.
   */
  cases_mutated: [sessionKey: string];
  /** The agent successfully changed the current user's Case Discovery prompt library. */
  quick_actions_mutated: [sessionKey: string];
  /** The backend authoritatively confirmed a personal skill mutation. */
  skills_mutated: [sessionKey: string];
  /**
   * The backend reported that a turn running out of reach of this socket (other backend
   * instance, or replay window over — see `turn_in_progress`) has ended and is persisted.
   * The bridge drops the partial local turn and reloads the history from the API.
   */
  turn_settled: [sessionKey: string, conversationId: string, localTurnId: string | null];
  /**
   * Hector commence à écrire dans le canvas d'un fil (#1588). Au plus un signal
   * par `(conversationId, cleDIdempotence)` pour la vie du pool ; jamais sur un
   * refus du transport, avant l'écriture elle-même (voir
   * `SignalDEcritureAgentCanvas`). Seul abonné : le dock
   * (`useBasculeDuDockVersLAssistant`).
   */
  agent_canvas_write_started: [signal: SignalDEcritureAgentCanvas];
  /**
   * Hector prend le document d'un fil, après admission et avant son déploiement
   * (#1588). L'admission ordinaire peut être répétée : publier la même scène ne
   * change rien. La seule admission qui exige un slot visible est dédupliquée
   * par `(conversationId, cleDIdempotence)`, car elle déclenche une navigation.
   * Seul abonné : le dock (`useBasculeDuDockVersLAssistant`).
   */
  agent_canvas_admission_started: [signal: SignalDAdmissionAgentCanvas];
}
